// JoinChurchPage — redesign Phase 8b, the church-wide join link.
//
// Public route at /join/{slug} (see App.js's prefix-matched gate). Unlike a
// per-person invite (which has a specific invitations row + email), this is
// a church-wide link anyone can tap — so there's no InviteAcceptScreen-style
// "you've been invited as X" lookup, just the church itself.
//
// Props:
//   slug        — the church's churches.slug
//   onNavigate(path) — App.js's navigateTo, passed down like LandingPage/
//                      PrivacyPolicy already do

import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../supabase'

const NAVY   = 'var(--ink)'
const ACCENT = 'var(--gold)'
const SANS   = "'Plus Jakarta Sans',sans-serif"

const s = {
  wrap:    { minHeight:'100vh', background:'var(--cream)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', fontFamily:SANS },
  card:    { background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:20, padding:'32px 28px', width:'100%', maxWidth:400 },
  eyebrow: { fontSize:12, fontWeight:800, letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--gold-ink)', marginBottom:14 },
  headRow: { display:'flex', gap:14, alignItems:'center', marginBottom:18 },
  logoImg: { width:56, height:56, borderRadius:16, objectFit:'cover', flexShrink:0 },
  logoFallback: { width:56, height:56, borderRadius:16, background:ACCENT, color:NAVY, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, flexShrink:0 },
  name:    { fontSize:20, fontWeight:800, color:'var(--ink)' },
  meta:    { fontSize:13, color:'var(--stone)', marginTop:2 },
  sub:     { fontSize:14, color:'var(--stone)', marginBottom:22, lineHeight:1.5 },
  btn:     { width:'100%', background:ACCENT, color:NAVY, border:'none', borderRadius:10, padding:'14px', fontSize:15, fontWeight:800, cursor:'pointer', fontFamily:SANS },
  btnDis:  { opacity:0.5, cursor:'default' },
  err:     { background:'rgba(200,55,42,0.1)', border:'1px solid rgba(200,55,42,0.25)', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#C8372A', marginTop:16 },
  done:    { background:'rgba(47,90,49,0.1)', border:'1px solid rgba(47,90,49,0.25)', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#2F5A31', marginTop:16, lineHeight:1.5 },
}

export default function JoinChurchPage({ slug, onNavigate }) {
  const { user, refreshProfile } = useAuth()
  const [church, setChurch] = useState(undefined) // undefined = loading, null = not found
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [requested, setRequested] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    supabase.from('churches')
      .select('id, name, logo_url, location, meeting_info, invite_only')
      .eq('slug', slug)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setChurch(data || null) })
    return () => { cancelled = true }
  }, [slug])

  async function joinNow() {
    if (!church || busy) return
    setBusy(true); setError('')
    try {
      // join_church() decides open-vs-invite-only server-side (see
      // supabase-schema-security-church-id.sql) — no client-trusted
      // invite_only check needed any more.
      const { data: result, error: rpcErr } = await supabase.rpc('join_church', { p_church_id: church.id })
      if (rpcErr) throw rpcErr
      if (result === 'requested') {
        setRequested(true)
      } else {
        await supabase.from('user_church_follows')
          .upsert({ user_id: user.id, church_id: church.id }, { onConflict: 'user_id,church_id' })
        await refreshProfile?.()
        onNavigate?.('/app')
      }
    } catch (e) {
      setError(e?.message || 'Something went wrong — please try again.')
    } finally {
      setBusy(false)
    }
  }

  function joinAsNewUser() {
    try { sessionStorage.setItem('nlc.joinChurchSlug', slug) } catch (_e) { /* ignore */ }
    onNavigate?.('/login')
  }

  // "Get the best experience in the app" — same UA-sniffing pattern as
  // InviteAcceptScreen (src/components/Auth.jsx), reused here.
  const _ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : ''
  const _inApp = /median|gonative/i.test(_ua)
  const _isAndroid = /android/i.test(_ua)
  const _isIos = /iphone|ipad|ipod/i.test(_ua)
  const storeUrl = _isAndroid
    ? 'https://play.google.com/store/apps/details?id=uk.co.thbiblecommunity.app'
    : _isIos
      ? 'https://apps.apple.com/gb/app/id6776408991'
      : null
  const showAppBanner = !_inApp && !!storeUrl

  if (church === undefined) {
    return <div style={s.wrap}><div style={{ color:'var(--stone)', fontSize:14 }}>Loading…</div></div>
  }
  if (!church) {
    return (
      <div style={s.wrap}>
        <div style={s.card}>
          <div style={s.eyebrow}>The Bible Community</div>
          <div style={s.name}>Church not found</div>
          <div style={s.sub}>This join link isn't valid any more — ask your church for an up to date one.</div>
          <button style={s.btn} onClick={() => onNavigate?.('/')}>Go to The Bible Community</button>
        </div>
      </div>
    )
  }

  const initial = (church.name || '?').trim()[0]?.toUpperCase() || '?'
  const metaLine = [church.location, church.meeting_info].filter(Boolean).join(' · ')

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.eyebrow}>You're invited to join</div>
        <div style={s.headRow}>
          {church.logo_url && !logoFailed
            ? <img src={church.logo_url} alt="" style={s.logoImg} onError={()=>setLogoFailed(true)}/>
            : <div style={s.logoFallback}>{initial}</div>}
          <div style={{ minWidth:0 }}>
            <div style={s.name}>{church.name}</div>
            {metaLine && <div style={s.meta}>{metaLine}</div>}
          </div>
        </div>
        <div style={s.sub}>
          Sunday's sermon, all week long — a 5-minute daily devotional, and a place to pray for each other.
          It's free: your church covers it.
        </div>

        {showAppBanner && (
          <a href={storeUrl} target="_blank" rel="noopener noreferrer"
            style={{
              display:'flex', alignItems:'center', gap:12, textDecoration:'none',
              background:'rgba(232,200,122,0.08)', border:'1px solid rgba(232,200,122,0.3)',
              borderRadius:12, padding:'11px 14px', marginBottom:18,
            }}>
            <span style={{ fontSize:22, flexShrink:0 }}>📲</span>
            <span style={{ flex:1, minWidth:0 }}>
              <span style={{ display:'block', fontSize:13.5, fontWeight:700, color:'var(--ink)', fontFamily:SANS }}>Get the best experience in the app</span>
              <span style={{ display:'block', fontSize:12, color:'var(--stone)', fontFamily:SANS }}>You can finish setting up here, or open the app.</span>
            </span>
            <span style={{ flexShrink:0, background:ACCENT, color:NAVY, borderRadius:8, padding:'8px 12px', fontSize:12.5, fontWeight:800, fontFamily:SANS }}>Get app</span>
          </a>
        )}

        {requested ? (
          <div style={s.done}>Request sent — {church.name} will approve you shortly.</div>
        ) : user ? (
          <button style={{ ...s.btn, ...(busy ? s.btnDis : {}) }} disabled={busy} onClick={joinNow}>
            {busy ? 'Joining…' : church.invite_only ? `Request to join ${church.name}` : `Join ${church.name}`}
          </button>
        ) : (
          <button style={s.btn} onClick={joinAsNewUser}>
            {church.invite_only ? `Request to join ${church.name}` : `Join ${church.name}`}
          </button>
        )}
        {error && <div style={s.err}>{error}</div>}
      </div>
    </div>
  )
}
